import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiError, USE_MOCK, userAdminApi } from '@/api/client';
import { MOCK_USERS, type AdminUser } from '@/data/mockUsers';
import type { UserItem, CreateUserApiRequest } from '@/api/types';
import { useUserContext } from '@/contexts/UserContext';

/** Calls fn and returns fallback on 404 or network errors (service down). Re-throws other errors.
 *  Mismo patrón que safeCall en useAnalytics.ts (no se exporta desde ahí para no acoplar módulos). */
async function safeCall<T>(fn: () => Promise<T>, fallback: T): Promise<T> {
  try {
    return await fn();
  } catch (err: unknown) {
    if (err instanceof ApiError && err.status === 404) return fallback;
    if (err instanceof TypeError) return fallback; // ERR_CONNECTION_REFUSED / Failed to fetch
    throw err;
  }
}

/** Maps backend UserRole enum name → frontend i18n key used as roleKey */
function adaptRoleKey(roles: string[]): string {
  if (roles.includes('AdminGlobal')) return 'admin.roleAdminCentral';
  if (roles.includes('AdminOU')) return 'admin.roleAdminOU';
  return 'admin.roleAdminOU';
}

/** Map API UserItem (UserDto shape) → AdminUser (adds UI-only fields with defaults) */
function adaptUser(u: UserItem): AdminUser {
  const resolvedOuId = u.allOusAccess ? 'todas' : (u.ouId ?? '');
  const ouLabel = u.allOusAccess ? 'Todas' : (u.ouName ?? (u.ouId ? u.ouId : 'Todas'));
  return {
    id: u.id,
    name: u.displayName ?? u.email.split('@')[0],
    email: u.email,
    roleKey: adaptRoleKey(u.roles),
    ou: ouLabel,
    ouId: resolvedOuId,
    alcance: ouLabel === 'Todas' ? 'Global' : ouLabel,
    canCreateSurveys:  u.canCreateSurveys  ?? false,
    canViewAnalytics:  u.canViewAnalytics  ?? false,
    canManageUsers:    u.canManageUsers    ?? false,
    canApproveSurveys: u.canApproveSurveys ?? false,
    active: u.isActive,
  };
}

export function useUsers() {
  const { demoUser } = useUserContext();
  const tenantId = demoUser.tenant;

  return useQuery<AdminUser[]>({
    queryKey: ['users', tenantId],
    queryFn: async () => {
      if (USE_MOCK) return MOCK_USERS;
      const res = await userAdminApi.getUsers({ tenantId });
      return (res.items ?? []).map(adaptUser);
    },
  });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateUserApiRequest) => {
      if (USE_MOCK) {
        if (import.meta.env.DEV) console.log('[mock] createUser', body);
        const mockItem: UserItem = {
          id: String(Date.now()), email: body.email, displayName: body.displayName,
          tenantId: 'LATAM', roles: body.roles ?? ['AdminOU'], isActive: true,
        };
        return Promise.resolve(mockItem);
      }
      return userAdminApi.createUser(body);
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['users'] }); },
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: { ouId?: string; preferredLanguage?: string } }) => {
      if (USE_MOCK) {
        if (import.meta.env.DEV) console.log('[mock] updateUser', id, body);
        return Promise.resolve({ id, email: '', tenantId: '', roles: [], isActive: true, ...body });
      }
      return userAdminApi.updateUser(id, body);
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['users'] }); },
  });
}

export function useUpdateOwnLanguage() {
  return useMutation({
    mutationFn: ({ id, preferredLanguage }: { id: string; preferredLanguage: string }) => {
      if (USE_MOCK) {
        if (import.meta.env.DEV) console.log('[mock] updateOwnLanguage', id, preferredLanguage);
        return Promise.resolve({
          id, email: '', tenantId: '', roles: [], isActive: true, preferredLanguage,
        } as UserItem);
      }
      return userAdminApi.updateOwnLanguage(id, preferredLanguage);
    },
  });
}

export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => {
      if (USE_MOCK) {
        if (import.meta.env.DEV) console.log('[mock] deleteUser', id);
        return Promise.resolve();
      }
      return userAdminApi.deleteUser(id);
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['users'] }); },
  });
}

export function useToggleUserActive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) => {
      if (USE_MOCK) { console.log('[MOCK] toggleUserActive', id, isActive); return Promise.resolve({} as UserItem); }
      return userAdminApi.toggleUserActive(id, isActive);
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['users'] }); },
  });
}

export function useUpdateUserRoles() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, roles }: { id: string; roles: string[] }) => {
      if (USE_MOCK) { console.log('[MOCK] updateUserRoles', id, roles); return Promise.resolve({} as UserItem); }
      return userAdminApi.updateUserRoles(id, roles);
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['users'] }); },
  });
}

export function useUpdateUserPermissions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, perms }: {
      id: string;
      perms: { canCreateSurveys: boolean; canViewAnalytics: boolean; canManageUsers: boolean; canApproveSurveys: boolean };
    }) => {
      if (USE_MOCK) {
        if (import.meta.env.DEV) console.log('[mock] updateUserPermissions', id, perms);
        return Promise.resolve({} as UserItem);
      }
      return userAdminApi.updateUserPermissions(id, perms);
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['users'] }); },
  });
}

export interface AudiencePerson {
  id: string;
  name: string;
  initials: string;
  jobTitle: string;
  ouId: string;
}

export function initialsFromName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0] + parts[1]![0]).toUpperCase();
}

export function adaptAudiencePerson(u: UserItem): AudiencePerson {
  const name = u.displayName ?? u.email;
  return {
    id: u.id,
    name,
    initials: initialsFromName(name),
    jobTitle: u.jobTitle ?? '',
    ouId: u.ouId ?? '',
  };
}

const AUDIENCE_DEFAULT_PAGE_SIZE = 10;

/** Personas reales de una OU para StepAudiencia (modo "personalizada") — reemplaza MOCK_PEOPLE (E-009). */
export function useAudiencePeople(ouId: string, search: string, page = 1, pageSize = AUDIENCE_DEFAULT_PAGE_SIZE) {
  const { demoUser } = useUserContext();
  const tenantId = demoUser.tenant;

  return useQuery<{ people: AudiencePerson[]; totalCount: number }>({
    queryKey: ['users', 'audience', tenantId, ouId, search, page, pageSize],
    enabled: !!ouId,
    queryFn: async () => {
      const res = await userAdminApi.getUsers({ tenantId, page, pageSize, ouId, search });
      return { people: res.items.map(adaptAudiencePerson), totalCount: res.totalCount };
    },
  });
}

/** Resuelve displayName en lote para una lista de evaluatedId/userId — usado por el ranking
 *  generacional (GeneracionalTab) porque GET /v1/analytics/generational/participants no incluye
 *  nombre de persona por diseño (ver ParticipantOmissionDto/GenerationalParticipant en AnalyticsService). */
export function useUsersBatchLookup(userIds: string[]) {
  return useQuery<Array<{ id: string; displayName: string }>>({
    queryKey: ['users', 'batch-lookup', userIds],
    enabled: userIds.length > 0,
    retry: false,
    queryFn: () => USE_MOCK
      ? Promise.resolve([])
      : safeCall(() => userAdminApi.batchLookupUsers(userIds), []),
  });
}
